import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowRight, FiX, FiCamera } from "react-icons/fi";
import LogoutModal from "../../pages/profile/component/sidebar/LogoutModal";
import axiosInstance from "../../api/axiosInstance";

const PRIMARY_TEXT = "text-slate-800";
const ACTIVE_BG = "bg-[#134DA7]";

const ProfileSidebar = ({ isSidebarOpen, onClose }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  const links = [
    { name: "My Profile", path: "/profile-section" },
    { name: "My Bookings", path: "/booking" },
    { name: "Password Setting", path: "/password" },
    { name: "Help", path: "/help" },
    { name: "Logout", path: "/profile/logout", isLogout: true },
  ];

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setLoadingProfile(false);
        return;
      }

      try {
        setLoadingProfile(true);
        const response = await axiosInstance.get(`/users/${userId}`);

        if (response.data.success) {
          const user = response.data.user;
          setUserProfile(user);

          setImagePreview(
            user.profilePic ||
              "https://via.placeholder.com/120/4F46E5/ffffff?text=User"
          );
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        setImagePreview(
          "https://via.placeholder.com/120/E5E7EB/6B7280?text=No+Image"
        );
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImagePreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleUpload = async () => {
    if (!file || !userId) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("profilePic", file);

    try {
      const response = await axiosInstance.put(
        "/users/profile/upload",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        const newImageUrl = response.data.imageUrl;

        setImagePreview(newImageUrl);
        setUserProfile((prev) => ({ ...prev, profilePic: newImageUrl }));
        setFile(null);

        alert("Profile picture updated successfully!");
      }
    } catch (error) {
      alert(
        "Upload failed: " +
          (error.response?.data?.message || "Please try again")
      );
    } finally {
      setIsUploading(false);
    }
  };

  const getLinkClass = (path) => {
    const isActive =
      location.pathname === path && path !== "/profile/logout";

    const base =
      "flex justify-between items-center w-full px-4 py-3 rounded-xl font-bold transition-all duration-200 text-left text-[13px] border";

    return isActive
      ? `${base} ${ACTIVE_BG} text-white border-[#134DA7] shadow-sm`
      : `${base} bg-transparent border-transparent text-slate-600 hover:bg-slate-50 hover:text-[#134DA7]`;
  };

  const handleTabClick = (link) => {
    if (link.isLogout) {
      setIsModalOpen(true);
    } else {
      navigate(link.path);
      if (window.innerWidth < 768) onClose();
    }
  };

  return (
    <>
      <div
        className={`fixed top-0 left-0 h-full w-[260px] bg-white p-5 md:rounded-3xl shadow-2xl z-50 transform transition-transform md:translate-x-0 md:static md:w-72 md:shrink-0 md:block md:shadow-sm md:border md:border-slate-100 overflow-y-auto ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Close Button Mobile */}
        <button
          onClick={onClose}
          className="md:hidden absolute top-4 right-4 text-slate-400 hover:text-slate-600 bg-slate-100 p-2 rounded-full"
        >
          <FiX className="w-4 h-4" />
        </button>

        {/* Profile Card */}
        <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl flex flex-col items-center mb-5">
          <div className="relative w-20 h-20 md:w-24 md:h-24 mb-3">
            <img
              src={imagePreview || "https://via.placeholder.com/120"}
              alt="Profile"
              className="w-full h-full rounded-full object-cover border-4 border-white shadow-xl"
              onError={(e) => {
                e.target.src =
                  "https://via.placeholder.com/120/E5E7EB/6B7280?text=User";
              }}
            />

            <button
              onClick={() => document.getElementById("fileInput").click()}
              className="absolute bottom-0 right-0 p-2 md:p-2.5 bg-white rounded-full border border-gray-200 text-blue-600 shadow-lg hover:bg-gray-50 transition"
              title="Change profile picture"
            >
              <FiCamera className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            <input
              id="fileInput"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* User Info */}
          {loadingProfile ? (
            <div className="text-center space-y-2">
              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse mx-auto"></div>
              <div className="h-3 bg-gray-200 rounded w-32 animate-pulse mx-auto"></div>
            </div>
          ) : userProfile ? (
            <div className="text-center">
              <h2 className={`text-base md:text-xl font-bold ${PRIMARY_TEXT} truncate max-w-[200px]`}>
                {userProfile.name ||
                  userProfile.fullName ||
                  userProfile.username ||
                  "User"}
              </h2>
              <p className="text-[10px] md:text-sm text-gray-500 mt-0.5 break-all max-w-[200px]">
                {userProfile.email ||
                  userProfile.emailid ||
                  "user@example.com"}
              </p>
            </div>
          ) : (
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">No Profile</p>
          )}

          {/* Upload Button */}
          {file && (
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="mt-4 md:mt-5 px-5 md:px-6 py-2 md:py-2.5 bg-[#134DA7] text-white font-bold text-[10px] md:text-sm uppercase tracking-widest rounded-lg hover:bg-[#0f3d8a] disabled:opacity-70 transition shadow-md"
            >
              {isUploading ? "Saving..." : "Save Picture"}
            </button>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="space-y-2 md:space-y-3">
          {links.map((link) => (
            <button
              key={link.name}
              onClick={() => handleTabClick(link)}
              className={getLinkClass(link.path)}
            >
              {link.name}
              {!link.isLogout && <FiArrowRight className="w-4 h-4 md:w-5 md:h-5" />}
            </button>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <LogoutModal
          onConfirm={async () => {
            try {
              await axiosInstance.post("/users/logout");
            } catch (_) {}
            localStorage.clear();
            sessionStorage.clear();
            setIsModalOpen(false);
            window.location.replace("/");
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProfileSidebar;
